/**
 * The class strings of the rule builder's small native controls.
 *
 * Native `<select>` and `<input>`, not `@wend/ui`'s Radix `Select`, on purpose:
 * a rule row holds three to five controls on one line, inside groups inside
 * groups, and a Radix select per control is a portal and a focus trap per
 * control — on a tree of sixty rules. A native select is keyboard-complete,
 * announced correctly by every screen reader, and renders its chosen value in
 * static markup (Radix fills its trigger only after mount, so a suite reading
 * the builder's markup would see every operator blank).
 *
 * The look is the concept's `.sb-select` / `.sb-input` / `.sb-num`, expressed in
 * the theme's tokens: a dark well, a strong hairline, the brand focus ring.
 */
import { cn, focusRing } from '@wend/ui';

export const RULE_CONTROL_CLASS = cn(
  'min-h-[31px] rounded-sm border border-line-strong bg-black/30 px-2.5 py-1 text-[0.84rem] text-text',
  'disabled:cursor-not-allowed disabled:opacity-60',
  focusRing,
);

export const RULE_SELECT_CLASS = cn(RULE_CONTROL_CLASS, 'cursor-pointer pr-7 [&>option]:bg-surface-2 [&>option]:text-text');

export const RULE_NUMBER_CLASS = cn(RULE_CONTROL_CLASS, 'w-[5.5rem] font-mono tabular-nums text-good');

export const RULE_TEXT_CLASS = cn(RULE_CONTROL_CLASS, 'w-40 min-w-0');

export const RULE_DATE_CLASS = cn(RULE_CONTROL_CLASS, 'w-auto [color-scheme:dark]');

/** The unit beside a number — "days", "₹". */
export const RULE_UNIT_CLASS = 'font-mono text-[0.72rem] text-muted';

/** The dashed "add" buttons under a group. */
export const RULE_ADD_BUTTON_CLASS = cn(
  'inline-flex min-h-8 cursor-pointer items-center gap-1.5 rounded-sm border border-dashed border-line-strong px-3 py-1.5',
  'text-[0.8rem] text-muted transition-colors hover:border-brand-b/60 hover:bg-brand-a/10 hover:text-text',
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-line-strong disabled:hover:bg-transparent',
  focusRing,
);

/** The small ✕ that removes a rule or a group. */
export const RULE_REMOVE_BUTTON_CLASS = cn(
  'grid size-7 shrink-0 cursor-pointer place-items-center rounded-sm border border-transparent text-muted',
  'transition-colors hover:border-bad/30 hover:bg-bad/12 hover:text-bad',
  focusRing,
);
